import * as React from 'react';

export type UnsavedDialogState =
  | { type: 'cancel-edit' }
  | { type: 'navigation'; proceed: () => void; reset: () => void };

export interface UseUnsavedChangesDialogParams {
  hasUnsavedChanges: boolean;
  discardChanges: () => void;
}

export interface UseUnsavedChangesDialogResult {
  unsavedDialog: UnsavedDialogState | null;
  setUnsavedDialog: React.Dispatch<React.SetStateAction<UnsavedDialogState | null>>;
  requestCancelEdit: () => void;
  confirmLeave: () => void;
  cancelLeave: () => void;
}

export function useUnsavedChangesDialog(
  params: UseUnsavedChangesDialogParams
): UseUnsavedChangesDialogResult {
  const { hasUnsavedChanges, discardChanges } = params;

  const [unsavedDialog, setUnsavedDialog] =
    React.useState<UnsavedDialogState | null>(null);

  const requestCancelEdit = React.useCallback(() => {
    if (hasUnsavedChanges) {
      setUnsavedDialog({ type: 'cancel-edit' });
    } else {
      discardChanges();
    }
  }, [discardChanges, hasUnsavedChanges]);

  const confirmLeave = React.useCallback(() => {
    setUnsavedDialog((current) => {
      if (!current) return null;

      if (current.type === 'navigation') current.proceed();
      else discardChanges();

      return null;
    });
  }, [discardChanges]);

  const cancelLeave = React.useCallback(() => {
    setUnsavedDialog((current) => {
      if (current?.type === 'navigation') current.reset();
      return null;
    });
  }, []);

  return {
    unsavedDialog,
    setUnsavedDialog,
    requestCancelEdit,
    confirmLeave,
    cancelLeave,
  };
}

